/**
 * Support at home's two plan dialogs, the same telling for an engine plan and a plan built by hand
 * (owner, 19 Oct 2026: Manual Control must share the engine's look and feel — "this was an omission
 * when you built the aged care module initially").
 *
 *  - SahCauseEffect: "Explain this" — numbered lines of what they do and what that does to the
 *    numbers, against the support alone, then the result under the navy rule. The aged care ledger.
 *  - SahFigurePopover: every "why?" on a card — one pop-over with pages (their contribution, the Age
 *    Pension, where the year's money goes), a Back that returns one step, and the year-by-year link.
 *
 * Both read a plain figures object, so neither knows whether the plan came from the engine.
 */
import React, { useState } from 'react';
import DialogShell, { DialogSection } from '../../components/ui/DialogShell.tsx';
import { FromTo, StepNumber, ResultMarker, LedgerResult } from '../../components/strategy/CauseEffectLedger.tsx';
import { LeverExplainers } from '../../components/strategy/LeverExplainers.tsx';
import { Button } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { otherCashflowLines, cashflowNotOnTiles } from './sahCashflowLines.ts';
import { SAH_BENCH } from '../../utils/benchmarkWords.ts';

/** One plan's figures, as the cards and dialogs show them. */
export interface SahFigures {
    contribution: number;
    pension: number;
    /** A full year with the plan in place; negative is a shortfall. */
    cashflow: number;
    legacy: number;
    investmentIncome: number;
    rates: { independence: number; everydayLiving: number } | null;
    /** Gross services by group and what they pay on each, for the contribution page. */
    breakdown: { clinical: number; independence: number; everydayLiving: number; independenceContribution: number; everydayContribution: number; privateExcess: number } | null;
    livingExpenses: number;
    taxPayable: number;
    pensionRows: Array<{ label?: string; value?: unknown }>;
    cashFlowBreakdown: unknown;
}

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** The figures from an engine snapshot (the server's evaluateSupportAtHomeStrategies row). */
export function figuresFromSnapshot(s: Record<string, unknown> | null | undefined): SahFigures {
    const snap = (s || {}) as Record<string, unknown>;
    const rates = snap.contributionRates as { independence?: number; everydayLiving?: number } | undefined;
    const b = snap.supportAtHomeCostsBreakdown as Record<string, unknown> | undefined;
    return {
        contribution: num(snap.totalSupportAtHomeCosts),
        pension: num(snap.pension ?? snap.agePension),
        cashflow: num(snap.netCashflow ?? snap.cashflow),
        legacy: num(snap.legacyValue),
        investmentIncome: num(snap.abpIncome) + num(snap.otherInvestmentIncome) + num(snap.rentalIncome),
        rates: rates ? { independence: num(rates.independence), everydayLiving: num(rates.everydayLiving) } : null,
        breakdown: b ? {
            clinical: num(b.clinical), independence: num(b.independence), everydayLiving: num(b.everydayLiving),
            independenceContribution: num(b.independenceContribution), everydayContribution: num(b.everydayContribution), privateExcess: num(b.privateExcess),
        } : null,
        livingExpenses: num(snap.livingExpenses),
        taxPayable: num(snap.taxPayable),
        pensionRows: (snap.agePensionBreakdown as Array<{ label?: string; value?: unknown }>) || [],
        cashFlowBreakdown: snap.cashFlowBreakdown,
    };
}

const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));
const pct = (r: number) => `${+(r * 100).toFixed(1)}%`;

/* ── Explain this ─────────────────────────────────────────────────────────────────────────────── */

export interface SahCauseEffectProps {
    /** "They spend $250,000 renovating or upgrading the home — paid from …". */
    doSentence: string;
    treatment: string[];
    plan: SahFigures;
    baseline: SahFigures | null;
    horizonYears: number;
    /** "your" / "their" / "Colin's" — whose pension and contribution (householdVoice). */
    possessive?: string;
    /** "you" / "they" — the household after the first mention (householdVoice; takes a plural verb). */
    they?: string;
    /** What happens to each pension account the plan draws on (abpDrawSentences). */
    drawLines?: Array<{ lead: string; body: string }>;
    /**
     * Where a yearly shortfall is drawn from, account by account and in what order (moneyStory.tsx's
     * MoneyStoryBlock), told after "Put together" as aged care tells it (owner, 21 Oct 2026).
     */
    moneyStory?: React.ReactNode;
    /** The debts the plan keeps or pays off, and why (DebtsInPlanNote), at the foot. */
    debtsNote?: React.ReactNode;
    /** The plan's pieces (sahLeverKinds), each explained above the lines: what it is and how it works. */
    leverKinds?: ReadonlyArray<string>;
}

export const SahCauseEffect = ({ doSentence, treatment, plan, baseline, horizonYears, possessive = 'their', they = 'they', drawLines = [], moneyStory = null, debtsNote = null, leverKinds = [] }: SahCauseEffectProps) => {
    const lines: React.ReactNode[] = [];
    lines.push(<><strong className="text-slate-900">{doSentence}</strong>{treatment[0] ? <> {treatment[0]}</> : null}</>);
    // The pension accounts it draws on drop to the minimum draw — said, not left to be discovered.
    drawLines.forEach((l) => lines.push(<span data-testid="sah-cause-effect-abp"><strong className="text-slate-900">{l.lead}</strong> {l.body}</span>));
    if (baseline) {
        const dp = plan.pension - baseline.pension;
        lines.push(Math.abs(dp) < 1
            ? <><strong className="text-slate-900">{cap(possessive)} Age Pension stays at {money(plan.pension)} a year.</strong></>
            : dp > 0
                ? <><strong className="text-slate-900">Less is counted, so {possessive} Age Pension rises</strong> <FromTo from={baseline.pension} to={plan.pension} unit=" a year" better="up" />.</>
                : <><strong className="text-slate-900">More is counted, so {possessive} Age Pension falls</strong> <FromTo from={baseline.pension} to={plan.pension} unit=" a year" better="up" />.</>);
        const rb = baseline.rates, rp = plan.rates;
        const ratesMoved = rb && rp && (Math.abs(rp.independence - rb.independence) > 0.0005 || Math.abs(rp.everydayLiving - rb.everydayLiving) > 0.0005);
        const dc = plan.contribution - baseline.contribution;
        // Rates that move with no contribution to show (none paid either way) said nothing true: "falls from $0 to $0"
        // (owner-caught, 23 Oct 2026). Then the line is left out; where the contribution holds, it says so.
        const noContribution = baseline.contribution < 1 && plan.contribution < 1;
        if (ratesMoved && rb && rp && !noContribution) {
            const down = rp.independence + rp.everydayLiving < rb.independence + rb.everydayLiving;
            lines.push(<><strong className="text-slate-900">The same assessment {down ? 'lowers' : 'raises'} {possessive} contribution for each hour</strong> — getting out and about {pct(rb.independence)} → {pct(rp.independence)}, help around the house {pct(rb.everydayLiving)} → {pct(rp.everydayLiving)} — {Math.abs(dc) >= 1 ? <>so {possessive} contribution {dc <= 0 ? 'falls' : 'rises'} <FromTo from={baseline.contribution} to={plan.contribution} unit=" a year" better="down" />.</> : <>but {possessive} contribution stays at {money(plan.contribution)} a year.</>}</>);
        } else if (ratesMoved && noContribution) {
            // nothing to say about a contribution neither plan pays
        } else if (Math.abs(dc) >= 1) {
            lines.push(<><strong className="text-slate-900">{cap(possessive)} contribution {dc < 0 ? 'falls' : 'rises'}</strong> <FromTo from={baseline.contribution} to={plan.contribution} unit=" a year" better="down" />.</>);
        } else {
            lines.push(<><strong className="text-slate-900">{cap(possessive)} contribution stays at {money(plan.contribution)} a year</strong> — the rates for each hour do not move.</>);
        }
        const di = plan.investmentIncome - baseline.investmentIncome;
        if (Math.abs(di) >= 100) {
            lines.push(<><strong className="text-slate-900">{di < 0 ? 'Less money stays invested, so investment income falls' : 'More money is invested, so investment income rises'}</strong> <FromTo from={baseline.investmentIncome} to={plan.investmentIncome} unit=" a year" better="up" />.{di < 0 ? ' That is the cost of the plan.' : ''}</>);
        }
        const baseShort = baseline.cashflow < 0;
        lines.push(plan.cashflow >= 0
            ? <><strong className="text-slate-900">Put together, {they} have {money(plan.cashflow)} a year to spare</strong> (with the support alone, {baseShort ? `${money(baseline.cashflow)} short` : `${money(baseline.cashflow)} to spare`}).{moneyStory ? <> {moneyStory}</> : null}</>
            : <><strong className="text-slate-900">Put together, {they} are {money(plan.cashflow)} a year short</strong>, met from savings (with the support alone, {baseShort ? `${money(baseline.cashflow)} short` : `${money(baseline.cashflow)} to spare`}).{moneyStory ? <> {moneyStory}</> : null}</>);
    } else if (moneyStory) {
        lines.push(<>{moneyStory}</>);
    }
    return (
        <div data-testid="sah-cause-effect">
            <LeverExplainers kinds={leverKinds} className="mb-5" />
            <ol className="m-0 list-none p-0">
                {lines.map((l, i) => (
                    <li key={i} className={`grid grid-cols-[2.25rem_1fr] gap-x-2 border-b border-slate-200 py-4 text-[15px] leading-relaxed text-slate-700 ${i === 0 ? 'pt-0' : ''}`}>
                        <StepNumber n={i + 1} />
                        <div>{l}</div>
                    </li>
                ))}
                <li className="grid grid-cols-[2.25rem_1fr] gap-x-2 border-t-2 border-brand-navy py-4 text-[15px] leading-relaxed text-slate-700" data-testid="sah-cause-effect-result">
                    <ResultMarker />
                    <LedgerResult horizonYears={horizonYears} estate={plan.legacy} baseEstate={baseline ? baseline.legacy : null} baseWord={SAH_BENCH.word} />
                </li>
            </ol>
            {treatment.length > 0 && (
                <>
                    <DialogSection className="mt-6">The pieces of this plan, and how the means tests treat each</DialogSection>
                    <ul className="mt-2 space-y-2 text-[14px] leading-relaxed text-slate-700">
                        {treatment.map((t, i) => <li key={i} className="border-b border-slate-100 pb-2">{t}</li>)}
                    </ul>
                </>
            )}
            {debtsNote}
        </div>
    );
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* ── The figure pop-over ──────────────────────────────────────────────────────────────────────── */

export type SahFigurePage = 'contribution' | 'pension' | 'cashflow';

export interface SahFigurePopoverProps {
    planTitle: string;
    plan: SahFigures;
    baseline: SahFigures | null;
    initialPage: SahFigurePage;
    /** One sentence on which test sets the pension, in the household's terms. */
    pensionWhy: React.ReactNode;
    possessive?: string;
    /** "you" / "they", and "you" / "them" (householdVoice). */
    they?: string;
    them?: string;
    onYearByYear?: () => void;
    onClose: () => void;
}

const Row = ({ label, sub, value, bold = false, onWhy }: { label: React.ReactNode; sub?: React.ReactNode; value: React.ReactNode; bold?: boolean; onWhy?: () => void }) => (
    <div className={`flex items-start justify-between gap-6 ${bold ? 'mt-1 border-t-2 border-brand-navy pb-1 pt-3.5' : 'border-b border-slate-100 py-3'}`}>
        <div className="min-w-0">
            <p className={bold ? 'text-[16px] font-bold text-brand-navy' : 'text-[15px] text-slate-800'}>{label}</p>
            {(sub || onWhy) && (
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-500">
                    {sub}
                    {onWhy && <> <button type="button" onClick={onWhy} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy">why?</button></>}
                </p>
            )}
        </div>
        <p className={`shrink-0 tabular-nums ${bold ? 'text-[18px] font-bold text-brand-navy' : 'text-[15px] font-semibold text-slate-900'}`}>{value}</p>
    </div>
);

export const SahFigurePopover = ({ planTitle, plan, baseline, initialPage, pensionWhy, possessive = 'their', they = 'they', them = 'them', onYearByYear, onClose }: SahFigurePopoverProps) => {
    const [page, setPageOnly] = useState<SahFigurePage>(initialPage);
    const [trail, setTrail] = useState<SahFigurePage[]>([]);
    const setPage = (next: SahFigurePage) => { setTrail((t) => [...t, page]); setPageOnly(next); };
    const goBack = () => { const prev = trail[trail.length - 1]; if (prev == null) return; setTrail((t) => t.slice(0, -1)); setPageOnly(prev); };
    const pageName = (p: SahFigurePage) => (p === 'pension' ? 'the Age Pension' : p === 'contribution' ? `${possessive} contribution` : 'the year');
    const b = plan.breakdown;
    const r = plan.rates;
    const title = page === 'contribution' ? `Why ${possessive} contribution is ${money(plan.contribution)} a year`
        : page === 'pension' ? `Why ${possessive} Age Pension is ${money(plan.pension)} a year`
        : plan.cashflow < 0 ? `Where the ${money(plan.cashflow)} shortfall comes from` : `Where the ${money(plan.cashflow)} left over comes from`;
    const intro = page === 'contribution'
        ? `Each service costs what the provider charges. What ${they} pay is a percentage of each hour, set by the means test, on the services within the budget; anything above the budget is paid at full price.`
        : page === 'pension' ? pensionWhy
        : 'Money in, less money out, over a full year with this plan in place. When it is short, the gap comes out of savings.';

    const other = cashflowNotOnTiles({
        pension: plan.pension, otherInvestmentIncome: plan.investmentIncome, livingExpenses: plan.livingExpenses,
        totalSupportAtHomeCosts: plan.contribution, taxPayable: plan.taxPayable, cashflow: plan.cashflow,
    });
    const otherRows = Math.abs(other) >= 1 ? otherCashflowLines(plan.cashFlowBreakdown, other) : [];

    return (
        <DialogShell
            titleId="sah-figure-heading"
            testId="sah-figure-popover"
            zIndex={60}
            eyebrow={planTitle}
            back={trail.length ? { label: `Back to ${pageName(trail[trail.length - 1])}`, onClick: goBack } : null}
            title={title}
            intro={intro}
            onClose={onClose}
            footer={
                <>
                    {onYearByYear && <button type="button" onClick={onYearByYear} className="mr-auto text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="sah-figure-year-by-year">See the year-by-year figures</button>}
                    {trail.length > 0 && <Button type="button" variant="secondary" onClick={goBack}>‹ Back</Button>}
                    <Button type="button" variant="primary" onClick={onClose}>Got it</Button>
                </>
            }
        >
            {page === 'contribution' && (
                <div data-testid="sah-figure-contribution">
                    {b ? (
                        <>
                            <DialogSection>Free to {them} within the budget</DialogSection>
                            <Row label="Nursing and personal care" sub={`${money(b.clinical)} of services`} value="$0" />
                            <DialogSection className="mt-6">Getting out and about</DialogSection>
                            <Row label="Social support, transport and similar" sub={r ? `${money(b.independence)} of services at ${pct(r.independence)} of each hour${baseline?.rates ? ` (with the support alone, ${pct(baseline.rates.independence)})` : ''}` : `${money(b.independence)} of services`} value={money(b.independenceContribution)} />
                            <DialogSection className="mt-6">Help around the house</DialogSection>
                            <Row label="Cleaning, meals and similar" sub={r ? `${money(b.everydayLiving)} of services at ${pct(r.everydayLiving)} of each hour${baseline?.rates ? ` (with the support alone, ${pct(baseline.rates.everydayLiving)})` : ''}` : `${money(b.everydayLiving)} of services`} value={money(b.everydayContribution)} />
                            {b.privateExcess >= 1 && (
                                <>
                                    <DialogSection className="mt-6">Above the budget</DialogSection>
                                    <Row label="Paid at full price" sub="Services beyond each person's yearly budget are bought privately; the rates above apply only within the budget." value={money(b.privateExcess)} />
                                </>
                            )}
                            <Row label={`${cap(possessive)} contribution, a year`} value={money(plan.contribution)} bold />
                        </>
                    ) : (
                        <Row label={`${cap(possessive)} contribution, a year`} value={money(plan.contribution)} bold />
                    )}
                    {baseline && Math.abs(baseline.contribution - plan.contribution) >= 1 && (
                        <p className="mt-4 text-[13px] text-slate-500">
                            With the support alone, {possessive} contribution is {money(baseline.contribution)}, so this plan {plan.contribution < baseline.contribution ? 'saves' : 'costs'} {money(baseline.contribution - plan.contribution)} a year.{' '}
                            <button type="button" onClick={() => setPage('pension')} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px]">Why the rates are what they are</button>
                        </p>
                    )}
                </div>
            )}

            {page === 'pension' && (
                <div data-testid="sah-figure-pension">
                    {(plan.pensionRows || []).filter((row) => row && row.label).map((row, i) => (
                        <Row key={i} label={String(row.label).replace(/^-+\s*|\s*-+$/g, '')} value={typeof row.value === 'number' ? formatCurrency(row.value) : String(row.value ?? '')} />
                    ))}
                    <Row label={`${cap(possessive)} Age Pension, a year`} value={money(plan.pension)} bold />
                    {baseline && Math.abs(baseline.pension - plan.pension) >= 1 && (
                        <p className="mt-4 text-[13px] text-slate-500">With the support alone, it is {money(baseline.pension)} a year.</p>
                    )}
                </div>
            )}

            {page === 'cashflow' && (
                <div data-testid="sah-figure-cashflow">
                    <DialogSection>Money in</DialogSection>
                    <Row label="Age Pension" value={money(plan.pension)} onWhy={() => setPage('pension')} />
                    <Row label="Investment income" sub="Interest, dividends, rent and account-based pensions" value={money(plan.investmentIncome)} />
                    <DialogSection className="mt-6">Money out</DialogSection>
                    <Row label="Living costs" value={`−${money(plan.livingExpenses)}`} />
                    <Row label={`${cap(possessive)} contribution to the support`} value={`−${money(plan.contribution)}`} onWhy={() => setPage('contribution')} />
                    <Row label={plan.taxPayable < 0 ? 'Tax refund' : 'Income tax'} value={`${plan.taxPayable < 0 ? '+' : '−'}${money(plan.taxPayable)}`} />
                    {otherRows.map((row, i) => <Row key={i} label={row.label} value={`${row.value < 0 ? '−' : '+'}${money(row.value)}`} />)}
                    <Row label={plan.cashflow < 0 ? 'Each year, short by' : 'Left over each year'} value={money(plan.cashflow)} bold />
                    {baseline && (
                        <p className="mt-4 text-[13px] text-slate-500">With the support alone, {baseline.cashflow < 0 ? `${they} are ${money(baseline.cashflow)} short` : `${they} have ${money(baseline.cashflow)} to spare`} each year.</p>
                    )}
                </div>
            )}
        </DialogShell>
    );
};
